import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../../shared/lib/auth';
import {
  ATIVOS_KEYS,
  ATIVOS_KEYS_S02,
  ATIVOS_KEYS_S03,
  listarAtivos,
  listarCatalogo,
  listarStockItems,
  criarStockItem,
  atualizarStockItem,
  listarStockPurchaseOrders,
  listarStockMovements,
  criarStockMovement,
  atualizarStockLocationDetails,
  uploadStockMovementInvoice,
  downloadStockMovementInvoice,
  listarStockIssues,
  devolverStockIssue,
  listarStockInventories,
  buscarStockInventory,
  criarStockInventory,
  iniciarStockInventory,
  contarStockInventoryItem,
  fecharStockInventory,
  buscarAtivo,
  criarAtivo,
  atualizarAtivo,
  excluirAtivo,
  listarCategorias,
  listarDocumentos,
  uploadDocumento,
  baixarDocumento,
  excluirDocumento,
  listarLocations,
  listarCostCenters,
  listarProjects,
  listarClients,
  listarFornecedores,
  listarCustodians,
  criarCustodian,
  listarCustodianTypeOptions,
  listarCustodianEntityOptions,
  entregarAtivo,
  devolverAtivo,
  transferirAtivo,
  listarAssignments,
  registrarAssinatura,
  listarMovementsByAsset,
  listarMovementsGlobal,
  exportMovementsCsv,
  iniciarManutencao,
  finalizarManutencao,
  listarCiclosManutencao,
  emprestarAtivo,
  substituirAtivo,
  baixarAtivo,
  lookupUsers,
  listarLicenses,
  buscarLicense,
  criarLicense,
  atualizarLicense,
  excluirLicense,
  atribuirSeat,
  revogarSeat,
  listarAlerts,
  contarAlertsOpen,
  reconhecerAlert,
  resolverAlert,
  rodarMotorAlerts,
  atualizarCustodianActive,
  atualizarCustodian,
  type UpdateCustodianPayload,
  ATIVOS_KEYS_S04,
  ATIVOS_KEYS_S05,
  listarInventarios,
  buscarInventario,
  criarInventario,
  iniciarInventario,
  fecharInventario,
  listarItensInventario,
  conferirItem,
  conferirPorCodigo,
  previewInventario,
  relatorioInventario,
  exportarInventarioCsv,
  inventariosDoAtivo,
  type InventoryScopeType,
  type InventoryResult,
  type CreateInventoryPayload,
  type CheckItemPayload,
  type CheckByCodePayload,
  type CreateLicensePayload,
  type UpdateLicensePayload,
  type ListAlertsParams,
  type ListAssetsParams,
  type CreateAssetPayload,
  type UpdateAssetPayload,
  type CreateStockItemPayload,
  type UpdateStockItemPayload,
  type CreateStockMovementPayload,
  type UpdateStockLocationDetailsPayload,
  type StockPurchaseOrder,
  type ReturnStockIssuePayload,
  type CreateStockInventoryPayload,
  type CountStockInventoryItemPayload,
  type AssetDocumentType,
  type CreateCustodianPayload,
  type DeliverPayload,
  type ReturnPayload,
  type TransferPayload,
  type CustodianType,
  type ListMovementsParams,
  type StartMaintenancePayload,
  type FinishMaintenancePayload,
  type LoanPayload,
  type ReplacePayload,
  type RetirePayload,
} from '../api';

export function useAtivos(params?: ListAssetsParams) {
  return useQuery({
    queryKey: ATIVOS_KEYS.list(params),
    queryFn: () => listarAtivos(params),
  });
}

/** Grid "Listar Ativos": ativos e licenças juntos (issue #464). */
export function useCatalogoAtivos(params?: ListAssetsParams) {
  return useQuery({
    queryKey: ATIVOS_KEYS.catalog(params),
    queryFn: () => listarCatalogo(params),
  });
}

export function useStockItems(params: Parameters<typeof listarStockItems>[0] = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS.stockItems(params),
    queryFn: () => listarStockItems(params),
  });
}

export function useStockPurchaseOrders() {
  return useQuery({
    queryKey: ['ativos', 'stock-purchase-orders'],
    queryFn: listarStockPurchaseOrders,
  });
}

export function useStockMovements(itemId: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS.stockMovements(itemId ?? ''),
    queryFn: () => listarStockMovements(itemId as string),
    enabled: Boolean(itemId),
  });
}

export function useCreateStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStockItemPayload) => criarStockItem(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] }),
  });
}

export function useUpdateStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateStockItemPayload }) =>
      atualizarStockItem(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] });
      qc.invalidateQueries({ queryKey: ['ativos', 'alerts'] });
    },
  });
}

export function useCreateStockMovement(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, invoiceFile }: { payload: CreateStockMovementPayload; invoiceFile?: File }) =>
      criarStockMovement(itemId, payload, invoiceFile),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] });
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.stockMovements(itemId) });
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-issues'] });
    },
  });
}

export function useUpdateStockLocationDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, payload }: { locationId: string; payload: UpdateStockLocationDetailsPayload }) =>
      atualizarStockLocationDetails(locationId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.masterData.locations });
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] });
    },
  });
}

export function useUploadStockMovementInvoice(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movementId, file }: { movementId: string; file: File }) =>
      uploadStockMovementInvoice(itemId, movementId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATIVOS_KEYS.stockMovements(itemId) }),
  });
}

export function useDownloadStockMovementInvoice(itemId: string) {
  return useMutation({
    mutationFn: (movementId: string) => downloadStockMovementInvoice(itemId, movementId),
  });
}

export function useStockIssues(params: Parameters<typeof listarStockIssues>[0] = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS.stockIssues(params),
    queryFn: () => listarStockIssues(params),
  });
}

export function useReturnStockIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ReturnStockIssuePayload }) =>
      devolverStockIssue(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-issues'] });
      qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] });
    },
  });
}

function invalidateStockInventory(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ['ativos', 'stock-inventories'] });
  if (id) qc.invalidateQueries({ queryKey: ATIVOS_KEYS.stockInventory(id) });
  qc.invalidateQueries({ queryKey: ['ativos', 'stock-items'] });
}

export function useStockInventories(params: Parameters<typeof listarStockInventories>[0] = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS.stockInventories(params),
    queryFn: () => listarStockInventories(params),
  });
}

export function useStockInventory(id: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS.stockInventory(id ?? ''),
    queryFn: () => buscarStockInventory(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateStockInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStockInventoryPayload) => criarStockInventory(payload),
    onSuccess: () => invalidateStockInventory(qc),
  });
}

export function useStartStockInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => iniciarStockInventory(id),
    onSuccess: (_result, id) => invalidateStockInventory(qc, id),
  });
}

export function useCountStockInventoryItem(inventoryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: CountStockInventoryItemPayload }) =>
      contarStockInventoryItem(inventoryId, itemId, payload),
    onSuccess: () => invalidateStockInventory(qc, inventoryId),
  });
}

export function useCloseStockInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fecharStockInventory(id),
    onSuccess: (_result, id) => invalidateStockInventory(qc, id),
  });
}

export function useAtivo(id: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS.detail(id ?? ''),
    queryFn: () => buscarAtivo(id as string),
    enabled: !!id,
  });
}

export function useCriarAtivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAssetPayload) => criarAtivo(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATIVOS_KEYS.all }),
  });
}

export function useAtualizarAtivo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAssetPayload) => atualizarAtivo(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.all });
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.detail(id) });
    },
  });
}

export function useExcluirAtivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirAtivo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATIVOS_KEYS.all }),
  });
}

export function useCategorias() {
  return useQuery({ queryKey: ATIVOS_KEYS.categories, queryFn: listarCategorias });
}

export function useDocumentos(assetId: string) {
  return useQuery({
    queryKey: ATIVOS_KEYS.documents(assetId),
    queryFn: () => listarDocumentos(assetId),
    enabled: !!assetId,
  });
}

export function useUploadDocumento(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, type }: { file: File; type: AssetDocumentType }) => uploadDocumento(assetId, file, type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.documents(assetId) });
      qc.invalidateQueries({ queryKey: ATIVOS_KEYS.detail(assetId) });
    },
  });
}

export function useDownloadDocumento(assetId: string) {
  return useMutation({
    mutationFn: (documentId: string) => baixarDocumento(assetId, documentId),
  });
}

export function useExcluirDocumento(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => excluirDocumento(assetId, documentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATIVOS_KEYS.documents(assetId) }),
  });
}

export function useLocations() {
  return useQuery({ queryKey: ATIVOS_KEYS.masterData.locations, queryFn: listarLocations });
}
export function useCostCenters() {
  return useQuery({ queryKey: ATIVOS_KEYS.masterData.costCenters, queryFn: listarCostCenters });
}
export function useProjects() {
  return useQuery({ queryKey: ATIVOS_KEYS.masterData.projects, queryFn: listarProjects });
}
export function useClients() {
  return useQuery({ queryKey: ATIVOS_KEYS.masterData.clients, queryFn: listarClients });
}
export function useFornecedores() {
  return useQuery({ queryKey: ['fornecedores'], queryFn: listarFornecedores });
}

// ============================================================
// SPEC 02 · Responsabilidade, termos e assinatura
// ============================================================

export function useCustodians(
  params: { type?: CustodianType; search?: string; active?: boolean; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S02.custodians(params),
    queryFn: () => listarCustodians({ limit: 30, ...params }),
  });
}

export function useCriarCustodian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustodianPayload) => criarCustodian(payload),
    onSuccess: () => {
      // invalidateQueries por prefix marca stale + refetch active.
      // refetchType: 'all' garante que queries inativas também refazem
      // fetch quando voltarem — caso a página tenha filtros diferentes
      // do form de criação.
      qc.invalidateQueries({ queryKey: ['custodians'], refetchType: 'all' });
    },
  });
}

/**
 * Catálogo do campo "Tipo" (#541). Cacheado por 5 min: perfis e as entidades
 * não pessoais mudam pouco e o modal abre com frequência.
 */
export function useCustodianTypeOptions() {
  return useQuery({
    queryKey: ATIVOS_KEYS_S02.custodianTypeOptions,
    queryFn: listarCustodianTypeOptions,
    staleTime: 5 * 60_000,
  });
}

/** Opções ativas de Departamento/Cliente conforme o Tipo escolhido. */
export function useCustodianEntityOptions(type?: 'department' | 'client') {
  return useQuery({
    queryKey: ATIVOS_KEYS_S02.custodianEntityOptions(type ?? 'department'),
    queryFn: () => listarCustodianEntityOptions(type as 'department' | 'client'),
    enabled: !!type,
    staleTime: 60_000,
  });
}

export function useAssignments(assetId: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S02.assignments(assetId ?? ''),
    queryFn: () => listarAssignments(assetId as string),
    enabled: !!assetId,
  });
}

function invalidateAsset(qc: ReturnType<typeof useQueryClient>, assetId: string) {
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS.detail(assetId) });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS.all });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S02.assignments(assetId) });
}

export function useEntregar(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeliverPayload) => entregarAtivo(assetId, payload),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

export function useDevolver(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, payload }: { assignmentId: string; payload: ReturnPayload }) =>
      devolverAtivo(assetId, assignmentId, payload),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

export function useTransferir(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TransferPayload) => transferirAtivo(assetId, payload),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

export function useAssinar(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => registrarAssinatura(assignmentId),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

// ============================================================
// SPEC 03
// ============================================================

function invalidateAssetLifecycle(qc: ReturnType<typeof useQueryClient>, assetId: string) {
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS.detail(assetId) });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS.all });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S02.assignments(assetId) });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S03.movementsByAsset(assetId) });
  qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S03.maintenanceCycles(assetId) });
  qc.invalidateQueries({ queryKey: ['ativos', 'movements', 'global'] });
}

export function useMovementsByAsset(assetId: string | undefined, params: ListMovementsParams = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S03.movementsByAsset(assetId ?? '', params),
    queryFn: () => listarMovementsByAsset(assetId as string, params),
    enabled: !!assetId,
  });
}

export function useMovementsGlobal(params: ListMovementsParams = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S03.movementsGlobal(params),
    queryFn: () => listarMovementsGlobal(params),
  });
}

export function useExportMovementsCsv() {
  return useMutation({
    mutationFn: (params: ListMovementsParams) => exportMovementsCsv(params),
  });
}

export function useCiclosManutencao(assetId: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S03.maintenanceCycles(assetId ?? ''),
    queryFn: () => listarCiclosManutencao(assetId as string),
    enabled: !!assetId,
  });
}

export function useIniciarManutencao(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StartMaintenancePayload) => iniciarManutencao(assetId, payload),
    onSuccess: () => invalidateAssetLifecycle(qc, assetId),
  });
}

export function useFinalizarManutencao(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FinishMaintenancePayload) => finalizarManutencao(assetId, payload),
    onSuccess: () => invalidateAssetLifecycle(qc, assetId),
  });
}

export function useEmprestar(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoanPayload) => emprestarAtivo(assetId, payload),
    onSuccess: () => invalidateAssetLifecycle(qc, assetId),
  });
}

export function useSubstituir(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReplacePayload) => substituirAtivo(assetId, payload),
    onSuccess: () => invalidateAssetLifecycle(qc, assetId),
  });
}

export function useBaixarAtivo(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RetirePayload) => baixarAtivo(assetId, payload),
    onSuccess: () => invalidateAssetLifecycle(qc, assetId),
  });
}

/**
 * Lookup de aprovadores da baixa patrimonial (SPEC 03).
 * Filtro fixo de roles (admin, governance, finance) — mesma faixa que pode
 * baixar. Busca por nome/email; enabled só quando query de busca tem >= 2
 * chars ou explicitamente pedimos lista inicial.
 */
export function useAprovadoresBaixa(search: string) {
  return useQuery({
    queryKey: ['users', 'lookup', 'retirement-approvers', search],
    queryFn: () =>
      lookupUsers({ roles: ['admin', 'governance', 'finance'], search: search || undefined }),
    enabled: search.length >= 2 || search === '',
    staleTime: 30_000,
  });
}

/**
 * Lookup genérico de usuários por role. Usado no cadastro de
 * custodiário (CLT precisa de um user com role=collaborator; PJ com
 * role=prestador_pj). Frontend lista até 100 e faz filtro client-side
 * pelo label.
 */
export function useLookupUsers(roles: string[], enabled = true) {
  return useQuery({
    queryKey: ['users', 'lookup', roles.slice().sort().join(',')],
    queryFn: () => lookupUsers({ roles, limit: 100 }),
    enabled: enabled && roles.length > 0,
    staleTime: 60_000,
  });
}


// ============================================================
// SPEC 04
// ============================================================

function invalidateLicenses(qc: ReturnType<typeof useQueryClient>, licenseId?: string) {
  qc.invalidateQueries({ queryKey: ['ativos', 'licenses'] });
  // A grid "Listar Ativos" também mostra licenças (issue #464).
  qc.invalidateQueries({ queryKey: ['ativos', 'catalog'] });
  if (licenseId) qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S04.license(licenseId) });
  // Motor pode invalidar alerts (license_limit_reached) → invalidar tudo.
  qc.invalidateQueries({ queryKey: ['ativos', 'alerts'] });
}

function invalidateAlerts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['ativos', 'alerts'] });
}

export function useLicenses(params: Parameters<typeof listarLicenses>[0] = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S04.licenses(params),
    queryFn: () => listarLicenses(params),
  });
}

export function useLicense(id: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S04.license(id ?? ''),
    queryFn: () => buscarLicense(id as string),
    enabled: !!id,
  });
}

export function useCriarLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLicensePayload) => criarLicense(payload),
    onSuccess: () => invalidateLicenses(qc),
  });
}

export function useAtualizarLicense(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateLicensePayload) => atualizarLicense(id, payload),
    onSuccess: () => invalidateLicenses(qc, id),
  });
}

export function useExcluirLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirLicense(id),
    onSuccess: () => invalidateLicenses(qc),
  });
}

export function useAtribuirSeat(licenseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ custodianId, assignedAt }: { custodianId: string; assignedAt?: string }) =>
      atribuirSeat(licenseId, custodianId, assignedAt),
    onSuccess: () => invalidateLicenses(qc, licenseId),
  });
}

export function useRevogarSeat(licenseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seatId: string) => revogarSeat(licenseId, seatId),
    onSuccess: () => invalidateLicenses(qc, licenseId),
  });
}

export function useAlerts(params: ListAlertsParams = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S04.alerts(params),
    queryFn: () => listarAlerts(params),
  });
}

// M5 (pós-review): roles sem acesso ao módulo Ativos não devem ficar
// polling `/alerts/count` a cada 60s recebendo 403 pra sempre. Habilita
// só pra READ_ROLES conhecidas do módulo.
const ALERTS_READ_ROLES = new Set([
  'admin', 'governance', 'purchasing', 'it', 'finance', 'rh', 'manager', 'audit',
]);

export function useAlertsCount() {
  const user = authService.getCurrentUser();
  const enabled = !!user && ALERTS_READ_ROLES.has(user.role);
  return useQuery({
    queryKey: ATIVOS_KEYS_S04.alertsCount,
    queryFn: contarAlertsOpen,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled,
  });
}

export function useReconhecerAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reconhecerAlert(id),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useResolverAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resolverAlert(id),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useRodarMotor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rodarMotorAlerts(),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useAtualizarCustodianActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      atualizarCustodianActive(id, active),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custodians'] });
      qc.invalidateQueries({ queryKey: ['ativos', 'alerts'] });
    },
  });
}

export function useAtualizarCustodian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCustodianPayload }) =>
      atualizarCustodian(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custodians'] });
    },
  });
}

// ============================================================
// SPEC 05
// ============================================================

function invalidateInventory(qc: ReturnType<typeof useQueryClient>, invId?: string) {
  qc.invalidateQueries({ queryKey: ['ativos', 'inventories'] });
  if (invId) {
    qc.invalidateQueries({ queryKey: ATIVOS_KEYS_S05.inventory(invId) });
    qc.invalidateQueries({ queryKey: ['ativos', 'inventory', invId, 'items'] });
  }
  qc.invalidateQueries({ queryKey: ['ativos', 'alerts'] });
  qc.invalidateQueries({ queryKey: ['ativos', 'alerts', 'count'] });
}

export function useInventarios(params: Parameters<typeof listarInventarios>[0] = {}) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.inventories(params),
    queryFn: () => listarInventarios(params),
  });
}

export function useInventario(id: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.inventory(id ?? ''),
    queryFn: () => buscarInventario(id as string),
    enabled: !!id,
  });
}

export function useItensInventario(
  id: string | undefined,
  params: { result?: InventoryResult; search?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.items(id ?? '', params),
    queryFn: () => listarItensInventario(id as string, params),
    enabled: !!id,
  });
}

export function useCriarInventario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInventoryPayload) => criarInventario(payload),
    onSuccess: () => invalidateInventory(qc),
  });
}

export function useIniciarInventario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => iniciarInventario(id),
    onSuccess: (_r, id) => invalidateInventory(qc, id),
  });
}

export function useFecharInventario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fecharInventario(id),
    onSuccess: (_r, id) => invalidateInventory(qc, id),
  });
}

export function useConferirItem(inventoryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: CheckItemPayload }) =>
      conferirItem(inventoryId, itemId, payload),
    onSuccess: () => invalidateInventory(qc, inventoryId),
  });
}

export function useConferirPorCodigo(inventoryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CheckByCodePayload) => conferirPorCodigo(inventoryId, payload),
    onSuccess: () => invalidateInventory(qc, inventoryId),
  });
}

export function usePreviewInventario(scopeType: InventoryScopeType, scopeRefId?: string) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.preview(scopeType, scopeRefId),
    queryFn: () => previewInventario(scopeType, scopeRefId),
    enabled: scopeType === 'all' || !!scopeRefId,
    staleTime: 10_000,
  });
}

export function useRelatorioInventario(id: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.report(id ?? ''),
    queryFn: () => relatorioInventario(id as string),
    enabled: !!id,
  });
}

export function useExportarInventarioCsv() {
  return useMutation({
    mutationFn: (id: string) => exportarInventarioCsv(id),
  });
}

export function useInventariosDoAtivo(assetId: string | undefined) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S05.byAsset(assetId ?? ''),
    queryFn: () => inventariosDoAtivo(assetId as string),
    enabled: !!assetId,
  });
}

// ============================================================
//   SPEC 06 — Dashboard
// ============================================================
// staleTime 5min sem polling (aprovado #8). O usuário re-abre a tela
// pra atualizar; refetchOnWindowFocus true dá refresh natural.

import { ATIVOS_KEYS_S06, obterDashboardAtivos, type DashboardParams } from '../api';

export function useDashboardAtivos(params?: DashboardParams) {
  return useQuery({
    queryKey: ATIVOS_KEYS_S06.dashboard(params),
    queryFn: () => obterDashboardAtivos(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
